/**
 * Mermaid Import Modal
 * Paste Mermaid flowchart text → parse → (auto-layout) → load into the editor.
 */

import { useMemo, useState, memo } from 'react'
import Modal from './Modal'
import { parseMermaidToDiagram } from '../../utils/mermaidImport'
import { parseMermaidClassDiagram } from '../../utils/mermaidClassImport'
import { parseMermaidStateDiagram } from '../../utils/mermaidStateImport'
import { parseMermaidC4Diagram } from '../../utils/mermaidC4Import'
import { parseMermaidSequenceDiagram } from '../../utils/mermaidSequenceImport'
import { parseMermaidGanttDiagram } from '../../utils/mermaidGanttImport'
import { parseMermaidErd } from '../../utils/mermaidErdImport'
import type { DiagramData } from '../../types/schema'
import './JSONImportModal.css'

export interface MermaidImportModalProps {
  isOpen: boolean
  onClose: () => void
  onImport: (data: DiagramData) => void
}

const PLACEHOLDER = `flowchart TD
  A[Start] --> B{Choice}
  B -->|Yes| C(Process)
  B -->|No| D((End))`

function MermaidImportModal({ isOpen, onClose, onImport }: MermaidImportModalProps) {
  const [text, setText] = useState('')

  const result = useMemo(() => {
    if (!text.trim()) return null
    // Route by diagram header: each structured type has its own parser.
    if (/^\s*classDiagram(-v2)?\b/m.test(text)) return parseMermaidClassDiagram(text)
    if (/^\s*stateDiagram(-v2)?\b/m.test(text)) return parseMermaidStateDiagram(text)
    if (/^\s*C4(Context|Container|Component|Dynamic|Deployment)\b/m.test(text)) return parseMermaidC4Diagram(text)
    if (/^\s*sequenceDiagram\b/m.test(text)) return parseMermaidSequenceDiagram(text)
    if (/^\s*gantt\b/m.test(text)) return parseMermaidGanttDiagram(text)
    if (/^\s*erDiagram\b/m.test(text)) return parseMermaidErd(text)
    return parseMermaidToDiagram(text)
  }, [text])

  const nodeCount = result?.data.nodes.length ?? 0
  const edgeCount = result?.data.edges?.length ?? 0
  const canImport = !!result && nodeCount > 0

  const handleClose = () => {
    setText('')
    onClose()
  }

  const handleImport = () => {
    if (!result || nodeCount === 0) return
    onImport(result.data)
    handleClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import from Mermaid">
      <div className="json-import-modal">
        <div className="json-import-content">
          <textarea
            className="json-input-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Paste Mermaid flowchart syntax…\n\n${PLACEHOLDER}`}
            spellCheck={false}
          />
        </div>

        {result && (
          <div className="json-validation-info">
            <div className="json-stats">
              <span>{nodeCount > 0 ? '✅' : '⚠️'} Parsed Mermaid</span>
              <span>• {nodeCount} nodes</span>
              <span>• {edgeCount} edges</span>
            </div>
            {result.warnings.length > 0 && (
              <div className="json-error" style={{ marginTop: 8 }}>
                <strong>Notes:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {result.warnings.slice(0, 5).map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="modal-actions">
          <button onClick={handleClose} className="button-secondary">
            Cancel
          </button>
          <button onClick={handleImport} className="button-primary" disabled={!canImport}>
            Import
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default memo(MermaidImportModal)
